import type {
  ArgsOf,
  Output,
  Parameter,
  RequestOf,
  StagedRequestOf,
} from "@p0security/shared/types/access/index";
import { Access } from "@p0security/shared/types/access/index";

const accountParameter = {
  description: "Snowflake account identifier",
  label: "Account",
  type: "static",
  isPreselect: true,
  optional: true,
} as const satisfies Parameter;

const accountIdParameter = {
  label: "Account ID",
  type: "generated",
} as const satisfies Parameter;

const userOutput = {
  type: "string",
} as const satisfies Output;

const userParameter = {
  description: "Snowflake user (required if multiple users with your email)",
  label: "User",
  type: "static",
  optional: true,
} as const satisfies Parameter;

const resourceParameter = {
  catalog: {
    label: "Catalog",
    type: "generated",
  } as const satisfies Parameter,
  relation: {
    label: "Relation",
    type: "generated",
  } as const satisfies Parameter,
  schema: {
    label: "Schema",
    type: "generated",
  } as const satisfies Parameter,
} as const;

const resourceParameterWithObjectType = {
  ...resourceParameter,
  objectType: {
    label: "Object type",
    type: "generated",
  } as const satisfies Parameter,
} as const;

// Parallels the `Grants` type from `psql-grants`
const grantsParameter = {
  label: "Grants",
  properties: {
    create: {
      label: "CREATE grants",
      multivalued: true,
      properties: resourceParameterWithObjectType,
      type: "object",
    },
    delete: {
      label: "DELETE grants",
      multivalued: true,
      properties: resourceParameter,
      type: "object",
    },
    insert: {
      label: "INSERT grants",
      multivalued: true,
      properties: resourceParameter,
      type: "object",
    },
    ownership: {
      label: "OWNERSHIP grants",
      multivalued: true,
      properties: resourceParameterWithObjectType,
      type: "object",
    },
    update: {
      label: "UPDATE grants",
      multivalued: true,
      properties: resourceParameter,
      type: "object",
    },
    select: {
      label: "SELECT grants",
      multivalued: true,
      properties: resourceParameter,
      type: "object",
    },
  },
  type: "object",
} as const satisfies Parameter;

const commonParameters = {
  account: accountParameter,
  accountId: accountIdParameter,
  user: userParameter,
} as const;

export const SnowflakeAccesses = {
  role: Access({
    description: "Snowflake role",
    maturity: "ga",
    delegation: {},
    generated: { user: userOutput },
    label: "Role",
    parameters: {
      ...commonParameters,
      name: { label: "Role name", type: "static" },
    },
    render: { locator: "accountId", policy: "name" } as const,
  }),
  sql: Access({
    description: "SQL query",
    maturity: "ga",
    delegation: {},
    generated: {
      role: { type: "string" },
      user: userOutput,
    },
    label: "SQL query",
    parameters: {
      ...commonParameters,
      database: {
        hint: "Database to use when not explicit in your query.",
        label: "Default database",
        optional: true,
        type: "input",
      },
      grants: grantsParameter,
      neededRoles: {
        label: "Required roles",
        type: "generated",
        multivalued: true,
      },
      schema: {
        hint: "Schema to use when not explicit in your query.",
        label: "Default schema",
        optional: true,
        type: "input",
      },
      text: {
        hint: "Copy and paste a query to run here. Separate multiple statements with semicolons. Only ANSI SQL queries are supported.",
        label: "Query text",
        multiline: true,
        type: "input",
      },
      warehouse: {
        description: "The warehouse where you will run the query.",
        label: "Warehouse",
        optional: true,
        type: "input",
      },
    },
    render: { locator: "accountId", resource: "text" } as const,
  }),
};

export type SnowflakeAccesses = typeof SnowflakeAccesses;
export type SnowflakeAccessType = keyof SnowflakeAccesses;
export type SnowflakeAccess = SnowflakeAccesses[SnowflakeAccessType];
export type SnowflakeAccessArgs = ArgsOf<SnowflakeAccess>;

export type SnowflakeRoleAccess = SnowflakeAccesses["role"];
export type SnowflakeRoleAccessArgs = ArgsOf<SnowflakeRoleAccess>;
export type SnowflakeRoleRequest = RequestOf<SnowflakeRoleAccess>;
export type SnowflakeRoleStagedRequest = StagedRequestOf<SnowflakeRoleAccess>;

export type SnowflakeSqlAccess = SnowflakeAccesses["sql"];
export type SnowflakeSqlAccessArgs = ArgsOf<SnowflakeSqlAccess>;
export type SnowflakeSqlRequest = RequestOf<SnowflakeSqlAccess>;
export type SnowflakeSqlStagedRequest = StagedRequestOf<SnowflakeSqlAccess>;

export type SnowflakeRequest = RequestOf<SnowflakeAccess>;
export type SnowflakeStagedRequest = StagedRequestOf<SnowflakeAccess>;
